import { formatNumber } from '@progress/kendo-intl';
import { SvgIcon } from "@progress/kendo-react-common";
import { DropDownList, DropDownListProps } from "@progress/kendo-react-dropdowns";
import { Loader } from "@progress/kendo-react-indicators";
import { MaskedTextBox, MaskedTextBoxHandle, MaskedTextBoxProps, NumericTextBox, NumericTextBoxHandle, NumericTextBoxProps, TextBoxProps } from "@progress/kendo-react-inputs";
import { checkIcon, hyperlinkOpenIcon, inboxIcon, paperPlaneIcon, xIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from "react";
import { DefaultInputComponentProps, parsePhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input/input";
import { isValidUrl } from "../utils/utils";
import EmailBounceDialog from "./EmailBounceDialog";
import SimpleDatePicker from "./SimpleDatePicker";

interface BaseProps<T> {
  data: T;
  readOnly?: boolean;
  required?: boolean;
  open?: () => void;
  edit?: () => void;
  save: (data: T) => Promise<void>;
  renderDisplay?: (data: T) => JSX.Element | string;
  renderEditable?: (data: T, saving: boolean, onChange: (data: T) => void) => JSX.Element;
  prefix?: JSX.Element;
  suffix?: JSX.Element;
}

interface PropsGeneric<T> extends BaseProps<T> {
  dataType?: never;
  inputProps?: never;
}

interface PropsString<T> extends BaseProps<T> {
  dataType: "string";
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

interface PropsEmail<T> extends BaseProps<T> {
  dataType: "email";
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

interface PropsNumber<T> extends BaseProps<T> {
  dataType: "number";
  inputProps?: NumericTextBoxProps;
}

interface PropsDate<T> extends BaseProps<T> {
  dataType: "date" | "dateonly";
  inputProps?: TextBoxProps;
  expiry?: boolean;
}

interface PropsBoolean<T> extends BaseProps<T> {
  dataType: "boolean";
  inputProps?: DropDownListProps;
}

interface PropsPhone<T> extends BaseProps<T> {
  dataType: "phone";
  inputProps?: MaskedTextBoxProps;
}

interface PropsIntlPhone<T> extends BaseProps<T> {
  dataType: "intlphone";
  inputProps?: DefaultInputComponentProps;
}

interface PropsWebAddress<T> extends BaseProps<T> {
  dataType: "webaddress";
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

type Props<T> = PropsGeneric<T> | PropsString<T> | PropsEmail<T> | PropsNumber<T> | PropsDate<T> | PropsBoolean<T> | PropsPhone<T> | PropsIntlPhone<T> | PropsWebAddress<T>;

const yesNoOptions = [{ text: 'Yes', value: 'true' }, { text: 'No', value: 'false' }];

export const EditableField = <T extends any>(props: Props<T>) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(props.data);
  const [showEmailBounceDialog, setShowEmailBounceDialog] = useState(false);

  const phoneInput = useRef<MaskedTextBoxHandle>();
  const numberInput = useRef<NumericTextBoxHandle>();

  // Update internal data
  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const stopEditing = () => {
    setData(props.data);
    setEditing(false);
  }

  const startEditing = () => {
    if (props.readOnly) return;
    setEditing(true);
    props.edit?.();
  }

  useEffect(() => {
    if (editing) {
      if (props.dataType === "phone") {
        phoneInput.current.focus();
      }
      if (props.dataType === "number") {
        numberInput.current.focus();
      }
    }
  }, [editing, props.dataType]);

  const save = async () => {
    setSaving(true);
    try {
      await props.save(data);
      setEditing(false);
    } catch {
      // Error intentionally ignored
    }
    setSaving(false);
  }

  const saveForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    save();
  }

  const captureEscape = (e: any) => {
    if (e.key === "Escape") {
      stopEditing();
    }
  }

  const formattedData = () => {
    switch (props.dataType) {
      case "string":
      case "email":
      case "phone":
      case "webaddress":
        return props.data as string;
      case "intlphone": {
        const value = parsePhoneNumber(props.data as string, "US");
        return value ? value.countryCallingCode == '1' ? value.formatNational() : value.formatInternational() : props.data as string;
      }
      case "number":
        return formatNumber(props.data as number, props.inputProps?.format || "n0");
      case "boolean":
        return props.data ? 'Yes' : 'No'
      case "date":
        return dayjs.utc(props.data as dayjs.ConfigType).tz("America/New_York").format("MM/DD/YYYY");
      case "dateonly":
        return props.data ? dayjs(props.data as dayjs.ConfigType).format("MM/DD/YYYY") : '';
    }
  }

  const expiryClass = () => {
    if (props.dataType !== "date" && props.dataType !== "dateonly") return '';
    if (!props.expiry || !props.data) return '';
    const inEastern = props.dataType === "date";
    const expires = inEastern ? dayjs.utc(props.data as dayjs.ConfigType).tz("America/New_York") : dayjs(props.data as dayjs.ConfigType);
    if (!expires.isValid()) return '';
    const today = inEastern ? dayjs().tz("America/New_York") : dayjs();
    const days = dayjs.utc(expires.format("YYYY-MM-DD")).diff(dayjs.utc(today.format("YYYY-MM-DD")), 'day');
    if (days <= 15) return ' text-danger font-weight-bold';
    if (days <= 30) return ' text-warning font-weight-bold';
    return '';
  }

  const renderDisplay = () => {
    return <span onClick={(props.readOnly && props.open) ? props.open : startEditing} className={`${props.readOnly ? 'disabled' : 'editable-field'} text-left btn btn-block${expiryClass()}`}>
      {props.renderDisplay ? props.renderDisplay(props.data) : formattedData()}
    </span>
  }

  const renderInputField = () => {
    switch (props.dataType) {
      case "string":
      case "webaddress":
        return <input autoFocus required={props.required} disabled={saving} className="form-control" type="text" value={data as string} onChange={(e) => {
          setData(e.target.value as T);
        }} {...props.inputProps} />;
      case "email":
        return <input autoFocus required={props.required} disabled={saving} className="form-control" type="email" value={data as string} onChange={(e) => {
          setData(e.target.value as T);
        }} {...props.inputProps} />;
      case "number":
        return <NumericTextBox
          ref={numberInput}
          disabled={saving}
          required={props.required}
          className="k-numeric-wrap-append flex-fill"
          value={data as number}
          onChange={(e) => setData(e.value as T)}
          {...props.inputProps}
        />;
      case "boolean":
        return <DropDownList
          disabled={saving}
          required={props.required}
          className="k-dropdown-wrap-append flex-fill"
          value={yesNoOptions.find(x => x.value === data.toString())}
          textField="text"
          dataItemKey="value"
          data={yesNoOptions}
          onChange={(e) => setData((e.target.value.value === 'true') as T)}
          {...props.inputProps}
        />;
      case "date":
      case "dateonly":
        return <SimpleDatePicker
          required={props.required}
          value={props.dataType === "date" || !data ? data as Date : dayjs(data as string).toDate()}
          min={new Date(1753, 1, 1)}
          max={new Date(2999, 11, 31)}
          onChange={(e) => props.dataType === "date" || !e ? setData(e as T) : setData(dayjs(e as Date).format("YYYY-MM-DD") as T)}
          inputProps={{
            disabled: saving,
            className: "k-textbox-wrap-append",
            ...props.inputProps
          }}
        />;
      case "phone":
        return <MaskedTextBox
          ref={phoneInput}
          disabled={saving}
          required={props.required}
          validationMessage="Please enter a valid phone number!"
          mask="(000) 000-0000"
          className="k-maskedtextbox-wrap-append flex-fill"
          value={data as string}
          onChange={(e) => e.value === '(___) ___-____' ? setData('' as T) : setData(e.value as T)}
          {...props.inputProps}
        />
      case "intlphone":
        return <PhoneInput
          autoFocus
          disabled={saving}
          required={props.required}
          className="form-control"
          placeholder="Use + for international numbers"
          value={data as any}
          defaultCountry="US"
          onChange={(value) => setData(value as T)}
          {...props.inputProps}
        />
    }
  }

  const renderEditable = () => {
    return <form className="input-group flex-nowrap" onKeyDown={captureEscape} onSubmit={saveForm}>
      {props.prefix && <div className="input-group-prepend">{props.prefix}</div>}
      {props.renderEditable ? props.renderEditable(data, saving, setData) : renderInputField()}
      <div className="input-group-append">
        {props.suffix && props.suffix}
        {props.open && <button className="btn btn-outline-secondary" disabled={saving} type="button" onClick={props.open}>
          <SvgIcon icon={hyperlinkOpenIcon} />
        </button>}
        {props.dataType === "webaddress" && data && isValidUrl(data as string) && <button className="btn btn-outline-success" type="button" onClick={() => {
          const url = data as string;
          const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
          window.open(formattedUrl, "_blank");
        }}>
          <SvgIcon icon={hyperlinkOpenIcon} />
        </button>}
        {(props.dataType === "phone" || props.dataType === "intlphone") && data && <button className="btn btn-outline-success" type="button" onClick={() => {
          const value = parsePhoneNumber(data as string, "US");
          if (value) window.open(`tel:${value.number}`);
        }}>
          <SvgIcon icon={{
            name: 'phone-call',
            content: '<path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z" />',
            viewBox: '0 0 24 24'
          }} />
        </button>}
        {props.dataType === "email" && data && <>
          <button className="btn btn-outline-secondary" type="button" disabled={saving} onClick={() => setShowEmailBounceDialog(true)}>
            <SvgIcon icon={inboxIcon} />
          </button>
          <button className="btn btn-outline-success" type="button" onClick={() => {
            window.open(`mailto:${data}`);
          }}>
            <SvgIcon icon={paperPlaneIcon} />
          </button>
        </>}
        <button className="btn btn-outline-primary" disabled={saving} type="submit">
          {saving ? <Loader size="small" /> : <SvgIcon icon={checkIcon} />}
        </button>
        <button className="btn btn-outline-warning" disabled={saving} type="button" onClick={stopEditing}>
          <SvgIcon icon={xIcon} />
        </button>
      </div>
    </form>;
  };

  return <>
    {editing ? renderEditable() : renderDisplay()}
    {showEmailBounceDialog && <EmailBounceDialog email={data as string} onClose={() => setShowEmailBounceDialog(false)} />}
  </>;
}